import dayjs from 'dayjs';
import { message } from 'antd';

export const getStatusType = (
  code?: string
): 'primary' | 'warning' | 'success' | 'default' => {
  if (code === 'NEW') return 'primary';
  if (code === 'IN_PROGRESS') return 'warning';
  if (code === 'CLOSED' || code === 'RESOLVED') return 'success';
  return 'default';
};

export const formatCustomerType = (type?: string, t?: any) => {
  if (!type) return '';
  const key = type.toLowerCase();
  if (t && t(`support.customerTypes.${key}`)) {
    return t(`support.customerTypes.${key}`);
  }
  const map: Record<string, string> = {
    BUSINESS_SELLER: 'Business seller',
    BUSINESS_BUYER: 'Business buyer',
    INDIVIDUAL_BUYER: 'Individual buyer',
    INDIVIDUAL_SELLER: 'Individual seller',
  };
  if (map[type]) return map[type];
  return type
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
};

export const formatFileSize = (bytes?: number) => {
  if (!bytes || isNaN(bytes)) return '0 KB';
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

export const formatActivityDate = (dateString?: string, t?: any) => {
  if (!dateString) return '';
  const date = dayjs(dateString);
  if (!date.isValid()) return dateString;
  const now = dayjs();
  const diffHours = Math.floor(now.diff(date, 'hour'));
  const diffDays = Math.floor(now.diff(date, 'day'));

  const formattedDate = date.format('DD MMM YYYY');
  if (diffHours < 1) {
    const justNow = t ? t('support.justNow', 'Just now') : 'Just now';
    return `${justNow} | ${formattedDate}`;
  }
  if (diffHours < 24) {
    const hoursText = t
      ? diffHours === 1
        ? t('support.hourAgo', '1 hour ago')
        : t('support.hoursAgo', {
            count: diffHours,
            defaultValue: `${diffHours} hours ago`,
          })
      : `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    return `${hoursText} | ${formattedDate}`;
  }
  const daysText = t
    ? diffDays === 1
      ? t('support.dayAgo', '1 day ago')
      : t('support.daysAgo', {
          count: diffDays,
          defaultValue: `${diffDays} days ago`,
        })
    : `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  return `${daysText} | ${formattedDate}`;
};

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];

export const formatDocumentUrl = (url?: string): string => {
  if (!url) return '';
  const trimmed = url.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

export const isImageFile = (fileNameOrUrl?: string): boolean => {
  if (!fileNameOrUrl) return false;
  const clean = fileNameOrUrl.split('?')[0].split('#')[0];
  const ext = clean.split('.').pop()?.toLowerCase();
  return ext ? IMAGE_EXTENSIONS.includes(ext) : false;
};

export const handleDownload = async (
  url?: string,
  fileName?: string,
  t?: any
) => {
  if (!url) {
    message.error(
      t
        ? t('support.fileNotFound', 'Attachment file not found')
        : 'Attachment file not found'
    );
    return;
  }

  const formattedUrl = formatDocumentUrl(url);

  try {
    const response = await fetch(formattedUrl);
    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`);
    }
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = fileName || 'document';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(a);
  } catch (error) {
    console.error('Error downloading file:', error);
    message.error(
      t
        ? t(
            'support.downloadFailed',
            'Unable to download file. It may no longer exist on the server.'
          )
        : 'Unable to download file. It may no longer exist on the server.'
    );
  }
};

export const viewDocument = (
  doc: { blobFileUrl?: string | null; name?: string; blobName?: string },
  setPreviewImage: (url: string | null) => void,
  t?: any
) => {
  if (!doc.blobFileUrl) {
    message.error(
      t
        ? t('support.fileNotFound', 'Attachment file not found')
        : 'Attachment file not found'
    );
    return;
  }

  const formattedUrl = formatDocumentUrl(doc.blobFileUrl);
  const docName = doc.name || doc.blobName || '';

  if (isImageFile(docName) || isImageFile(formattedUrl)) {
    const img = new window.Image();
    img.onload = () => {
      setPreviewImage(formattedUrl);
    };
    img.onerror = () => {
      message.error(
        t
          ? t(
              'support.fileNotFound',
              'Attachment file not found or is no longer available on the server'
            )
          : 'Attachment file not found or is no longer available on the server'
      );
    };
    img.src = formattedUrl;
  } else {
    const ext = (docName.split('.').pop() || '').toLowerCase();
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) {
      window.open(
        `https://docs.google.com/viewer?url=${encodeURIComponent(
          formattedUrl
        )}&embedded=true`,
        '_blank'
      );
    } else {
      window.open(formattedUrl, '_blank');
    }
  }
};
